<template>
  <ContentWrapper :add-padding="false">
  <fieldset :disabled="isGenerating">
    <form @submit="formSubmit">
      <Collapsable :toggle-on-mount="true" title="Automatically Generated Images">
      <p>Select one or more of the automatically generated low-resolution images below to create a new image at the desired resolution and zoom level. If you select more than one image, a <b>mix</b> of the selected images will be generated. You can replace all unselected images with new ones by clicking the refresh button.</p><p><i>All calculations are performed locally on your device, and generating an image with a large <b>Resolution</b> might, depending on your browser and hardware, take long. It is thus recommended to start with lower resolutions, and gradually increase it to avoid freezing the browser.</i></p>
      </Collapsable>
      <div class="formElement formElement--grid">
        <div class="refresh-container">
          <button :class="{ 'refresh-button--spin': isRefreshing }" class="refresh-button" @click.stop="refreshCandidates" type="button"><div class="refresh-button__icon">↻</div></button>
        </div>
        <div class="thumbnail-container" v-for="{ id, image, selected } in thumbnails" :key="id">
          <canvas :data-id="id" @click="handleClick(id)" ref="thumbnail" class="thumbnail" width="64" height="64" />
            <div @click="handleClick(id)" class="thumbnail-selector" :class="{ 'thumbnail-selector--selected': selected > -1 }">{{ selected > -1 ? '✔' : '' }}</div>
        </div>
      </div>

      <FormRange name="imgDim" min="10" max="1000" step="10" :value.sync="imgDim" label="Resolution" :formatValue="(value) => `${value}x${value} pixels`">
        <Tooltip slot="tooltip"
          >The model outputs only one pixel at a time, effectively allowing
          for the generation of infinite-resolution images. Try generating the
          same image at different resolutions, and observe the
          different visual properties that emerge.</Tooltip
        >
      </FormRange>
      <FormRange name="scale" min="0.01" max="10" step="0.01" :value.sync="scale" label="Zoom" :formatValue="(value) => parseFloat(scale).toFixed(2) + 'x'">
      <Tooltip slot="tooltip"><b>Zoom</b> allows you to generate zoomed-in or zoomed-out versions of the generated images. A value larger than 1 will zoom in, while a value smaller than 1 will zoom out.</Tooltip>
      </FormRange>
        <FormElement :vertical="false">
          <button class="form-button" type="submit">Generate Image</button>
          <button class="form-button" @click.stop.prevent="reset" type="button">
            Reset
          </button>
        </FormElement>
      </div>
    </form>
  </fieldset>
  </ContentWrapper>
</template>

<script>
import * as tf from '@tensorflow/tfjs'
import Tooltip from './Tooltip.vue'
import Collapsable from './Collapsable.vue'
import FormElement from './FormElement.vue'
import FormRange from './FormRange.vue'
import ContentWrapper from './ContentWrapper.vue'
import { getRandomLatentVector, generateImage } from '../util.js'


export default {
  name: 'InterpolationForm',
  components: {
    Collapsable,
    ContentWrapper,
    FormRange,
    FormElement,
    Tooltip
  },
  props: ['isGenerating', 'modelName'],
  data() {
    return {
      thumbnails: [...Array(4).fill(0).map((_, i) => ({
        id: i,
        image: null,
        selected: -1
      }))],
      imgDim: 30,
      scale: 1,
      collapsed: true,
      isRefreshing: true
    }
  },
  watch: {
    modelName: function(newVal, oldVal) {
      this.thumbnails.forEach(t => {
        const self = this
        const image = t.image
        const refIndex = self.$refs.thumbnail.findIndex(el => parseInt(el.dataset.id) === t.id)
        const canvas = self.$refs.thumbnail[refIndex]
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, 64, 64)
      })

      this.thumbnails = [...this.thumbnails.map(t => ({
        ...t,
        selected: -1
      }))]
      this.$nextTick(() => {
        this.refreshCandidates()
      })
    }
  },
  mounted() {
    this.refreshCandidates()
  },
  methods: {
    handleClick(selectedId) {
      const thumbnail = this.thumbnails.find(({ id }) => id === selectedId)
      const max = Math.max(...this.thumbnails.map(t => t.selected))
      const min = Math.min(...this.thumbnails.filter(t => t.selected > -1).map(t => t.selected))
      if (thumbnail && thumbnail.selected === -1) {
        // if (this.thumbnails.filter(t => t.selected > -1).length > 1) {
        //   window.alert("You can only select two images to morph between.")
        //   return
        // }
        thumbnail.selected = max + 1
      } else if (thumbnail){
        const newThumbnails = [...this.thumbnails.map(t => {
          if (t.selected > thumbnail.selected) {
            t.selected -= 1
          }
          return t
        })]
        this.thumbnails = newThumbnails
        thumbnail.selected = -1
      }
    },
    reset() {
      this.imgDim = 30
      this.scale = 1
      this.thumbnails = [...this.thumbnails.map(t => ({ ...t, selected: -1 }))]
      this.$ga.event({
        eventCategory: 'auto',
        eventAction: 'reset',
      })
    },
    async refreshCandidates() {
      this.isRefreshing = true
      const newThumbnails = [...this.thumbnails]
      const replaceIndices = newThumbnails.filter(t => t.selected === -1).map(t => newThumbnails.findIndex(t2 => t2.id === t.id))
      for (let i = 0; i < 4; i++) {
        if (replaceIndices.includes(i)) {
          // Dispose old tensor
          tf.dispose(newThumbnails[i].latentVector)

          const z = getRandomLatentVector(true)
          const image = await generateImage(this.modelName, { imgDim: 64, scale: 10, latentVector: z })
          const newThumbnail = { id: z.id, latentVector: z, image, selected: -1 }
          newThumbnails[i] = newThumbnail
          await tf.nextFrame()
        }
      }

      this.thumbnails = [...newThumbnails]
      this.$nextTick(() => {
        const self = this
        let i = 0
        let timeout = setInterval(() => {
          if (i > 2) {
            clearInterval(timeout)
          }
          const thumbnail = self.thumbnails[i]
          const image = thumbnail.image
          const refIndex = self.$refs.thumbnail.findIndex(el => parseInt(el.dataset.id) === thumbnail.id)
          const canvas = self.$refs.thumbnail[refIndex]
          const ctx = canvas.getContext('2d')
          canvas.width = 64
          canvas.height = 64
          ctx.putImageData(image, 0, 0)
          i += 1
        }, 50)
      })
      this.isRefreshing = false
    },
    formSubmit(e) {
      e.preventDefault()
      const { frames, imgDim, scale } = this
      const latentVectors = [...this.thumbnails.filter(({ selected }) => selected > -1).sort((a,b) => {
          if (a.selected < b.selected) {
            return -1
          } else if (a.selected > b.selected) {
            return 1
          }
          return 0
        }).map(({ latentVector }) => latentVector)]
      if (!latentVectors.length) {
        alert("You need to select at least 1 image :-)")
        this.$ga.event({
          eventCategory: 'auto',
          eventAction: 'generate',
          eventValue: 0
        })
        return
      }
      const mixed = tf.tidy(() => {
        const f = 1/latentVectors.length
        let latentVector = tf.mul(latentVectors[0], f)
        for (let i = 1; i < latentVectors.length; i++) {
          latentVector = latentVector.add(tf.mul(latentVectors[i], f))
        }
        return latentVector
      })

      const config = {
        imgDim: parseFloat(imgDim),
        scale: 10/parseFloat(scale),
        latentVectors: [mixed],
      }
      this.$ga.event({
        eventCategory: 'auto',
        eventAction: 'generate',
        eventValue: 1
      })

      this.$emit('update', config)
    }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "../style/variables.scss";
@import "../style/mixins.scss";

fieldset {
}

form {
  background-color: $color-dark-lighter;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  align-items: center;
}

.formElement {
  margin: 5px;
  padding: 5px;
  width: 100%;
  display: flex;
  flex-direction: column;
  font-weight: bolder;
}

label {
  display: flex;
  justify-content: flex-start;
  text-transform: uppercase;
  align-items: center;
}

.label__value {
  font-weight: normal;
  font-style: oblique;
  margin-left: 5px;
  text-transform: none;
}

.thumbnail {
  width: 100%;
  height: 100%;
  border: 1px solid black;
}

.thumbnail-selector {
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  width: 25px;
  height: 25px;
  right: 0;
  position: absolute;
  opacity: 0.5;
  border: 1.5px solid black;
  border-radius: 50%;
  margin-right: 5px;
  margin-top: 5px;
  background-color: white;
  box-shadow: 1px 1px 1px rgba(0,0,0,0.25);
}

.thumbnail-selector {
  @include disable-select;
}

.thumbnail-selector--selected {
  color: black;
  background-color: #7dcd85;
  opacity: 1;
}

.thumbnail-container {
  display: flex;
  position: relative;
  flex-wrap: wrap;
  flex-basis: 50%;
}

.formElement--grid {
  width: 50%;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: row;
}

.refresh-container {
  flex-basis: 100%;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.refresh-button {
  background-color: $color-white; 
  color: $color-dark;
  box-shadow: none;
  border-radius: 50%;
  border-bottom: none;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  margin-bottom: $margin;
}

.refresh-button:active {
  background-color: $color-dark;
  color: $color-white;
}

.refresh-button__icon {
  font-weight: bold;

  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
}

.refresh-button--spin {
  -webkit-animation:spin 1s linear infinite;
  -moz-animation:spin 1s linear infinite;
  animation:spin 1s linear infinite;
}
@-moz-keyframes spin { 100% { -moz-transform: rotate(360deg); } }
@-webkit-keyframes spin { 100% { -webkit-transform: rotate(360deg); } }
@keyframes spin { 100% { -webkit-transform: rotate(360deg); transform:rotate(360deg); } }


@media (max-width: 767px) {
  fieldset {
    width: 100%;
  }
  form {
    flex-grow: 1;
    flex-direction: column;
  }

  .formElement {
    width: auto;
    padding: 10px;
    margin: 5px;
  }

  label {
    margin-bottom: 5px;
  }

  .refresh-button {
    height: 50px;
    width: 50px;
  }

  .refresh-button__icon {
    padding-left: 2px;
    padding-right: 2px;
    padding-bottom: 2px;
    font-size: 1.7em;
  }
}

</style>
